import React from "react";
import moment from "moment";
import clsx from "clsx";
import * as Icon from "react-feather";

import WComponent from "../common/WComponent";
import ToolsTextEntryModal from "../clients/Tools/kit/ToolsTextEntryModal";
import ToolsPageHeader from "../clients/Tools/kit/ToolsPageHeader";
import ToolsAvatar from "../clients/Tools/kit/ToolsAvatar";
import PlaybookReviewService from "../../services/PlaybookReviewService";
import PlaybookRunService from "../../services/PlaybookRunService";

import "./agent-run-report.css";

class AgentRunDecisionHeader extends WComponent {
	constructor(props) {
		super(props);
		this.state = {
			isReturnOpen: false,
			returnReason: "",
		};
	}

	onOpenReturn = () => {
		this.update({ isReturnOpen: true, returnReason: "" });
	};

	onCloseReturn = () => {
		this.update({ isReturnOpen: false });
	};

	onReturnReasonChange = ({ text }) => {
		this.update({ returnReason: text });
	};

	onReturn = async () => {
		const reason = this.state.returnReason.trim();
		const isReturned = await this.props.onReturn({ reason });
		if (isReturned) await this.update({ isReturnOpen: false });
	};

	renderPrimaryActions() {
		const { review, isActing, busyAction, onSend, onSignOff } = this.props;
		if (!PlaybookReviewService.isInDecisionFlow({ review })) return null;
		const { viewer, stage, openCount } = review;
		const isSending = busyAction === "send";

		if (stage === "needs_decisions" && viewer.canDecide) {
			const label = openCount > 0 ? `${openCount} left to decide` : review.signoffRequired ? "Send for review" : "Finish";
			return (
				<button
					type="button"
					className="tk-button tk-button--primary tk-button--compact"
					onClick={onSend}
					disabled={openCount > 0 || isActing}
					title={openCount > 0 ? "Every high and medium suggestion and every journal needs a decision first" : ""}
				>
					{openCount === 0 && <Icon.Send size={13} />}
					{isSending ? "Re-checking the books…" : label}
				</button>
			);
		}
		if (stage === "waiting_on_signoff" && viewer.canSignOff) {
			return (
				<>
					<button type="button" className="tk-button tk-button--secondary tk-button--compact" onClick={this.onOpenReturn} disabled={isActing}>
						Return
					</button>
					<button type="button" className="tk-button tk-button--primary tk-button--compact" onClick={onSignOff} disabled={isActing}>
						<Icon.CheckCircle size={13} />
						Sign off
					</button>
				</>
			);
		}
		return null;
	}

	renderHeader() {
		const { run, review, isCancelling, isResuming, onBack, backLabel = "Back", onCancelRun, onResumeRun } = this.props;
		const isFinished = PlaybookRunService.isFinished({ status: run.status });
		const canResume = PlaybookRunService.canResume({ run });
		const status = PlaybookRunService.describeRunOutcome({ run: review ? { ...run, review_stage: review.stage } : run });
		const { agentName, clientName, periodLabel: period, startedBy, toolChangeNote, costLabel } = PlaybookRunService.describeRun({ run });
		const starter = run.run_trigger === "scheduled" ? "started automatically" : `started by ${startedBy}`;
		const statusBadge = <span className={`tk-badge tk-badge--${status.tone}`}>{status.label}</span>;
		const toolChangeBadge = toolChangeNote && (
			<span className="tk-badge tk-badge--warning" title={toolChangeNote.detail} aria-label={toolChangeNote.detail}>
				{toolChangeNote.label}
			</span>
		);

		return (
			<ToolsPageHeader
				compact
				onBack={onBack}
				backLabel={backLabel}
				lead={<ToolsAvatar name={clientName} size="lg" />}
				title={clientName}
				badges={
					<React.Fragment>
						{statusBadge}
						{toolChangeBadge}
					</React.Fragment>
				}
				subtitle={[agentName, period, starter, moment(run.created_at).format("MMM D, h:mm a"), costLabel].filter(Boolean).join(" · ")}
				actions={
					<React.Fragment>
						{this.renderPrimaryActions()}
						{canResume && (
							<button
								type="button"
								className="tk-button tk-button--primary tk-button--compact"
								onClick={onResumeRun}
								disabled={isResuming}
								title="Runs every step again. What the run did so far stays on record, set aside."
							>
								<Icon.RotateCw size={13} />
								{isResuming ? "Resuming…" : "Resume run"}
							</button>
						)}
						{!isFinished && (
							<button type="button" className="tk-button tk-button--secondary tk-button--compact" onClick={onCancelRun} disabled={isCancelling}>
								{isCancelling ? "Cancelling…" : "Cancel run"}
							</button>
						)}
					</React.Fragment>
				}
			/>
		);
	}

	renderStageBar() {
		const { review, isActing, onAcceptAllLow, onRecheck } = this.props;
		if (!PlaybookReviewService.isInDecisionFlow({ review })) return null;
		const { stage, viewer, afterCheck } = review;
		const { lead, isUnassigned, latestReturn, undecidedLowCount, decideNote } = PlaybookReviewService.describeStageBar({ review });

		return (
			<div className={clsx("agent-run-report__stage", isUnassigned && "agent-run-report__stage--unassigned")}>
				<div className="agent-run-report__stage-main">
					<span className="agent-run-report__stage-lead">{lead}</span>
					{stage === "needs_decisions" && latestReturn && (
						<span className="agent-run-report__stage-note">
							<Icon.CornerUpLeft size={12} /> Returned by {latestReturn.by}: {latestReturn.note}
						</span>
					)}
					{afterCheck && (
						<span className="agent-run-report__stage-note">
							Books re-checked {moment(afterCheck.takenAt).format("MMM D, h:mm a")} by {afterCheck.takenBy}
							{afterCheck.error ? ` — the re-check failed: ${afterCheck.error}` : ""}
						</span>
					)}
					{review.decisionRecordCount > 0 && (
						<span className="agent-run-report__stage-note">
							<Icon.Database size={12} /> {PlaybookReviewService.describeDecisionRecordCount({ count: review.decisionRecordCount })}
						</span>
					)}
					{decideNote && <span className="agent-run-report__stage-note">{decideNote}</span>}
					{!viewer.canSignOff && stage === "waiting_on_signoff" && viewer.whyNotSignOff && (
						<span className="agent-run-report__stage-note">{viewer.whyNotSignOff}</span>
					)}
				</div>
				<div className="agent-run-report__stage-actions">
					{stage === "needs_decisions" && viewer.canDecide && undecidedLowCount > 0 && (
						<button type="button" className="tk-text-action" onClick={onAcceptAllLow} disabled={isActing}>
							Accept all {undecidedLowCount} low
						</button>
					)}
					{(viewer.canDecide || viewer.canSignOff) && (
						<button type="button" className="tk-text-action tk-text-action--muted" onClick={onRecheck} disabled={isActing}>
							<Icon.RefreshCw size={12} />
							Re-check the books
						</button>
					)}
				</div>
			</div>
		);
	}

	renderReturnModal() {
		const { isActing } = this.props;
		const { isReturnOpen, returnReason } = this.state;
		return (
			<ToolsTextEntryModal
				isOpen={isReturnOpen}
				title="Return for another look"
				width={520}
				label="What needs another look?"
				help="The people who decide see this on the run. Their decisions stay and they can change them."
				text={returnReason}
				rows={4}
				confirmLabel="Return to preparer"
				isBusy={isActing}
				onTextChange={this.onReturnReasonChange}
				onCancel={this.onCloseReturn}
				onConfirm={this.onReturn}
			/>
		);
	}

	render() {
		const { children } = this.props;
		return (
			<React.Fragment>
				{this.renderHeader()}
				{children}
				{this.renderStageBar()}
				{this.renderReturnModal()}
			</React.Fragment>
		);
	}
}

export default AgentRunDecisionHeader;
